import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserDetails } from '#models';
import { AUTH_CONFIG } from './auth.config';
import { PlatformService } from '../platform.service';
import { MsalAuthInstance } from './auth.instance';
import { AccessStatus, UserRole } from '#enums';
import { AuthenticationParameters, AuthResponse, ClientAuthError, InteractionRequiredAuthError } from 'msal';
import { LoadingIndicatorService } from '../loading-indicator.service';

@Injectable()
export class AuthService {
  private _helper = inject(PlatformService);
  private _instance = inject(MsalAuthInstance);
  private _authConfig = inject(AUTH_CONFIG);
  private _loadingService = inject(LoadingIndicatorService);
  private _userDetails$ = new BehaviorSubject<UserDetails | null>(null);

  getAccountName() {
    const acct = this._instance.getAccount();
    if (acct) {
      return acct.name;
    }
    return '';
  }
  getAccountEmail(): string {
    const acct = this._instance.getAccount();
    if (acct) {
      return acct.userName;
    }
    return '';
  }

  logout() {
    const target = window.location.pathname + window.location.search;
    sessionStorage.clear();
    // Set AFTER clear so the URL survives the redirect — tab-scoped so each tab restores its own page
    if (target && target !== '/' && !target.startsWith('/500') && !target.startsWith('/403')) {
      sessionStorage.setItem('pssr_return_url', target);
    }
    this._instance.logout();
  }

  async acquireTokenSilent() {
    const acct = this._instance.getAccount();
    if (!acct) {
      throw new Error('No authenticated account available');
    }
    const req: AuthenticationParameters = {
      account: acct,
      loginHint: acct.userName,
      scopes: this._authConfig.scopes,
    };

    try {
      return await this._acquireTokenSilent(req);
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError || err instanceof ClientAuthError) {
        // Preserve the current URL so it can be restored after the login redirect completes
        const target = window.location.pathname + window.location.search;
        if (target && target !== '/' && !target.startsWith('/500') && !target.startsWith('/403')) {
          sessionStorage.setItem('pssr_return_url', target);
        }
        this._loadingService.show();
        this._instance.loginRedirect({
          scopes: this._authConfig.scopes,
          extraScopesToConsent: this._authConfig.extraScopesToConsent,
        });
        // Page is navigating away — never resolve so callers don't proceed with a missing token
        return Promise.resolve({} as AuthResponse);
      }
      throw err;
    }
  }

  private async _acquireTokenSilent(req: AuthenticationParameters) {
    // Wait for any in-progress login to settle before attempting silent acquisition
    if (this.isLoginInProgress()) {
      await new Promise<void>(resolve => setTimeout(resolve, 1_000));
    }

    if (this._helper.isIOS()) {
      return this._instance.acquireTokenSilent(req);
    }
    return this._instance.ssoSilent(req);
  }

  isAuthorized(roles: UserRole[] = []): boolean {
    const userDetails = this._userDetails$.getValue();
    if (!userDetails) {
      return false;
    }

    if (roles.includes(UserRole.User) && userDetails.hasAccess) {
      return true;
    }

    if (roles.includes(UserRole.BuAdmin) && userDetails.isBuAdmin) {
      return true;
    }

    if (roles.includes(UserRole.OrgAdmin) && userDetails.isOrgAdmin) {
      return true;
    }

    if (roles.includes(UserRole.SysAdmin) && userDetails.isSysAdmin) {
      return true;
    }

    return false;
  }

  getAccessStatus() {
    const details = this._userDetails$.value;

    if (details === null) {
      return AccessStatus.None;
    }
    return !details.hasAccess ? AccessStatus.Pending : AccessStatus.Granted;
  }

  getUserDetails(): UserDetails | null {
    return this._userDetails$.getValue();
  }

  get userDetails$() {
    return this._userDetails$;
  }

  setUserDetails(userDetails: UserDetails | null) {
    this._userDetails$.next(userDetails);
  }

  isLoginInProgress() {
    return this._instance.getLoginInProgress();
  }

  /**
   * Initialize authentication - handle redirect callback and attempt silent token acquisition
   * This should be called on app startup
   */
  async initialize(): Promise<void> {
    try {
      this._loadingService.show();

      // Handle redirect callback (for post-login redirects)
      // Use a timeout to prevent hanging if callback doesn't fire
      await Promise.race([
        this.handleRedirectCallback(),
        new Promise<boolean>(resolve => setTimeout(() => resolve(false), 5_000)),
      ]);

      // Clean up old MSAL cache entries to prevent 431 errors
      this.cleanupMsalCache();

      // Note: Token refresh is handled automatically by the auth interceptor
      // on each API request. No need to refresh proactively here.
    } catch (error) {
      console.error('Auth initialization error:', error);
    } finally {
      this._loadingService.hide();
    }
  }

  /**
   * Clean up old MSAL cache entries to prevent 431 Request Header Too Large errors
   * MSAL v1 doesn't automatically clean up expired tokens, which can accumulate over time
   */
  private cleanupMsalCache(): void {
    try {
      const now = Date.now();

      // Clean both localStorage and sessionStorage
      [localStorage, sessionStorage].forEach(storage => {
        const msalKeys = Object.keys(storage).filter(key => key.startsWith('msal.'));

        msalKeys.forEach(key => {
          try {
            const value = storage.getItem(key);
            if (!value) return;

            // Try to parse as JSON and check for expiration
            const parsed = JSON.parse(value);
            if (parsed && parsed.expiresOn) {
              const expiresOn =
                typeof parsed.expiresOn === 'string' ? new Date(parsed.expiresOn).getTime() : parsed.expiresOn;

              // Remove if expired more than 12 hours ago (prevents 431 errors)
              if (now - expiresOn > 43200000) {
                storage.removeItem(key);
              }
            }
          } catch {
            // If we can't parse the entry, leave it alone
          }
        });
      });
    } catch (error) {
      console.error('Error cleaning up MSAL cache:', error);
    }
  }

  /**
   * Handle MSAL redirect callback
   * Returns true if there was a redirect response
   */
  private async handleRedirectCallback(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      let resolved = false;

      const callback = (error: unknown, response: unknown) => {
        if (resolved) return;
        resolved = true;

        if (error) {
          console.error('Authentication redirect error:', error);
          resolve(false);
        } else {
          if (response) {
            resolve(true);
          } else {
            resolve(false);
          }
        }
      };

      try {
        this._instance.handleRedirectCallback(callback);

        // If callback doesn't fire within 1 second, resolve anyway
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            resolve(false);
          }
        }, 1_000);
      } catch (error) {
        console.error('Error setting up redirect callback:', error);
        if (!resolved) {
          resolved = true;
          resolve(false);
        }
      }
    });
  }

  /**
   * Initiate login flow
   */
  login() {
    if (this.isAuthenticated()) {
      return Promise.resolve();
    }

    if (this.isLoginInProgress()) {
      return Promise.resolve();
    }

    // Preserve deep-link URL so it can be restored after the Azure AD redirect cycle
    const target = window.location.pathname + window.location.search;
    if (target && target !== '/' && !target.startsWith('/500') && !target.startsWith('/403')) {
      sessionStorage.setItem('pssr_return_url', target);
    }

    // Show loading during redirect
    this._loadingService.show();

    this._instance.loginRedirect({
      scopes: this._authConfig.scopes, //
      extraScopesToConsent: this._authConfig.extraScopesToConsent, //
    });

    return Promise.resolve();
  }

  isAuthenticated(): boolean {
    const account = this._instance.getAccount();
    return account != null;
  }

  isSysAdmin() {
    const details = this._userDetails$.getValue();
    return details?.isSysAdmin;
  }
}
